/**
 * iter63z'' — Site Admin "Create League" modal.
 *
 * Skips the public request-then-approve flow. Used when a site admin
 * needs to spin up a league directly (e.g. for a partner asking
 * out-of-band, or for testing).
 *
 * On success, the modal shows the freshly-minted invite code so the
 * admin can hand it off immediately.
 */
import { useState } from "react";
import { X, Plus, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function SiteAdminCreateLeagueModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState({
        league_name: "",
        admin_username: "",
        admin_email: "",
        admin_is_player: true,
        teams_per_participant: 2,
        strict_invites: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [created, setCreated] = useState(null);
    const [copied, setCopied] = useState(false);

    if (!open) return null;

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const onSubmit = async (e) => {
        e?.preventDefault();
        if (!form.league_name.trim() || !form.admin_username.trim()) {
            toast.error("Rivalry name and admin username are required");
            return;
        }
        setSubmitting(true);
        try {
            const result = await api.siteCreateLeague(form);
            setCreated(result);
            toast.success(`League '${result.league.name}' created`);
            if (onCreated) onCreated(result);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Create failed");
        } finally { setSubmitting(false); }
    };

    const reset = () => {
        setForm({
            league_name: "", admin_username: "", admin_email: "",
            admin_is_player: true, teams_per_participant: 2, strict_invites: true,
        });
        setCreated(null); setCopied(false);
    };

    const copyInvite = () => {
        navigator.clipboard.writeText(created.league.invite_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-league-modal">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg max-w-lg w-full p-6 relative">
                <button
                    onClick={() => { reset(); onClose(); }}
                    className="absolute top-3 right-3 text-zinc-400 hover:text-white"
                    data-testid="create-league-close"
                >
                    <X className="w-5 h-5" />
                </button>

                {!created ? (
                    <>
                        <h2 className="text-lg font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                            <Plus className="w-5 h-5 text-cyber" /> Create Rivalry
                        </h2>
                        <p className="text-xs text-zinc-400 mb-4">
                            Skips the public request flow. The admin can join immediately with the invite code shown after creation.
                        </p>

                        <form onSubmit={onSubmit} className="space-y-3">
                            <Field
                                label="Rivalry name"
                                value={form.league_name}
                                onChange={(v) => set("league_name", v)}
                                testid="create-league-name"
                                placeholder="e.g. The Saturday Crew"
                                autoFocus
                            />
                            <Field
                                label="Admin username"
                                value={form.admin_username}
                                onChange={(v) => set("admin_username", v)}
                                testid="create-league-admin-username"
                                placeholder="display name shown to other crewmates"
                            />
                            <Field
                                label="Admin email (optional)"
                                value={form.admin_email}
                                onChange={(v) => set("admin_email", v)}
                                testid="create-league-admin-email"
                                placeholder="for password recovery"
                                type="email"
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-zinc-400 block mb-1">
                                        Teams per crewmate
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={form.teams_per_participant}
                                        onChange={(e) => set("teams_per_participant", Number(e.target.value))}
                                        className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm font-mono text-white focus:border-cyber focus:outline-none"
                                        data-testid="create-league-tpp"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] uppercase tracking-wider text-zinc-400 block mb-1">
                                        Flags
                                    </label>
                                    <label className="flex items-center gap-2 text-xs">
                                        <input
                                            type="checkbox"
                                            checked={form.admin_is_player}
                                            onChange={(e) => set("admin_is_player", e.target.checked)}
                                            data-testid="create-league-admin-is-player"
                                        />
                                        Admin also plays
                                    </label>
                                    {/* iter87.97 — Strict Invites is
                                        platform-wide default & non-toggleable. */}
                                </div>
                            </div>

                            <div className="pt-2 flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => { reset(); onClose(); }}
                                    className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white"
                                    data-testid="create-league-cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-1.5 bg-cyber/20 border border-cyber text-cyber rounded text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                                    data-testid="create-league-submit"
                                >
                                    {submitting ? "Creating…" : "Create Rivalry"}
                                </button>
                            </div>
                        </form>
                    </>
                ) : (
                    <>
                        <h2 className="text-lg font-bold uppercase tracking-wider mb-2 text-neon flex items-center gap-2">
                            <Check className="w-5 h-5" /> Created
                        </h2>
                        <div className="space-y-3 text-sm">
                            <KV label="League">{created.league.name}</KV>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Invite Code</div>
                                <div className="flex items-center gap-2">
                                    <code className="px-3 py-2 bg-zinc-950 border border-cyber rounded text-cyber font-mono text-lg flex-1 text-center" data-testid="created-invite-code">
                                        {created.league.invite_code}
                                    </code>
                                    <button
                                        onClick={copyInvite}
                                        className="px-2 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-xs"
                                        data-testid="copy-invite-code"
                                    >
                                        {copied ? <Check className="w-4 h-4 text-neon" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                                <p className="text-[10px] text-zinc-500 mt-1">
                                    Share with the admin. They'll use it to join via the landing page.
                                </p>
                            </div>
                            <KV label="Admin">{created.participant.username}{created.participant.email ? ` · ${created.participant.email}` : ""}</KV>
                            <KV label="League ID"><code className="text-xs">{created.league.id}</code></KV>
                        </div>

                        <div className="mt-5 flex items-center justify-end gap-2">
                            <button
                                onClick={reset}
                                className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white"
                                data-testid="create-league-create-another"
                            >
                                Create Another
                            </button>
                            <button
                                onClick={() => { reset(); onClose(); }}
                                className="px-4 py-1.5 bg-cyber/20 border border-cyber text-cyber rounded text-xs font-bold uppercase tracking-wider"
                                data-testid="create-league-done"
                            >
                                Done
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, testid, placeholder, type = "text", autoFocus }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 block mb-1">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoFocus={autoFocus}
                className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-white focus:border-cyber focus:outline-none"
                data-testid={testid}
            />
        </div>
    );
}

function KV({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</div>
            <div className="text-sm text-white">{children}</div>
        </div>
    );
}
